import { ReadResult } from 'interfaces/queryOptions'
import {
  Client,
  Installment,
  Order,
  OrderConnectivityStatus,
  OrderHardwareStatus,
  OrderStatus,
  Plan,
  Role
} from 'models'
import { FiscalData } from 'models/orderItem'
import { edit as baseEdit, patch, post } from 'services/billing'
import { checkRoles, cleanAmount } from 'utils'
import { ItemFiscalData } from '../types'
import { Coupon } from 'models/coupon'

export type EditOrder = {
  order: ReadResult<Order>
  client: ReadResult<Client>
  plan: ReadResult<Plan>
  coupon?: ReadResult<Coupon>
  installments?: Installment[]
}

const transformAttributes = (
  attributes: Record<string, any>
): Record<string, any> => {
  const { fine_mode, fine_amount } = attributes

  if (!fine_amount) {
    return {
      fine_mode,
      fine_amount
    }
  }

  return {
    fine_mode: fine_amount === '' || fine_amount === 0 ? 'fixed' : fine_mode,
    fine_amount:
      fine_amount === '' ? 0 : fine_mode === null ? 0 : cleanAmount(fine_amount)
  }
}

export async function edit(
  id: number,
  { order, client, plan, coupon, installments }: EditOrder
): Promise<ReadResult<Order>> {
  const { fine_mode, fine_amount } = transformAttributes(order.attributes)

  const payload: Partial<ReadResult<Order>> = {
    attributes: {
      quantity: order.attributes.quantity,
      amount: order.attributes.amount,
      shipping_method: order.attributes.shipping_method,
      ordered_at: order.attributes.ordered_at,
      fine_mode,
      fine_amount,
      penalty_period: order.attributes.penalty_period ?? null
    },
    relationships: {
      client: {
        type: 'client',
        id: client.id
      },
      plan: {
        type: 'plan',
        id: plan.id
      }
    }
  }

  if (coupon) {
    payload.relationships!.coupon = {
      type: 'coupon',
      id: coupon.id
    }
  }

  if (installments != null) {
    payload.relationships!.installments = installments.map((inst) => ({
      type: 'order_installments',
      attributes: {
        type: inst.type,
        amount:
          typeof inst.amount === 'string'
            ? cleanAmount(inst.amount as string)
            : inst.amount,
        due_at: inst.due_at,
        upfront: inst.upfront,
        eligible: inst.eligible
      }
    }))
  }

  const response = await baseEdit<Order>('/orders', id, payload)
  return response
}

export async function split(id: number, quantities: number[]): Promise<void> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  await patch(`/orders/${id}/split`, { data: { quantities } })
}

export async function updateStatus(
  id: number | string,
  status: OrderStatus,
  statusUpdatedAt: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  const { data } = await patch(`/orders/${id}/update-status/${status}`, {
    data: { status_updated_at: statusUpdatedAt }
  })
  return data
}

export async function cancel(
  id: number | string,
  fiscalData?: Partial<FiscalData>,
  canceledAt?: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.SALES])
  const attributes = {
    ...fiscalData,
    ...(canceledAt && { closed_at: canceledAt })
  }
  const payload = Object.keys(attributes).length > 0 ? { attributes } : {}
  const { data } = await patch(`/orders/${id}/cancel`, { data: payload })
  return data
}

export async function deliver(
  id: number | string,
  deliveredAt: Date
): Promise<any> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  const { data } = await patch(`/orders/${id}/deliver`, {
    data: { status_updated_at: deliveredAt }
  })
  return data
}

export async function updateItemFiscalData(
  orderId: number,
  data: ItemFiscalData
): Promise<void> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  await patch(`/orders/${orderId}/items/fiscal-data`, { data })
}

export async function updateItemStatus(
  orderId: number | string,
  status: OrderHardwareStatus | OrderConnectivityStatus,
  data: ItemFiscalData[]
): Promise<void> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.LOGISTICS])

  const dataWithStatus = data.map((d) => {
    return { attributes: { ...d.attributes, status } }
  })

  await patch(`/orders/${orderId}/items/update-status`, {
    data: dataWithStatus
  })
}

export async function mergeOrders(
  targetOrderId: number,
  sourceOrderIds: number[]
): Promise<void> {
  await checkRoles([Role.MANAGER, Role.FINANCE, Role.SALES])

  await post('/orders/merge', {
    attributes: {
      order_id: targetOrderId
    },
    relationships: {
      orders: sourceOrderIds.map((orderId) => ({
        id: orderId,
        type: 'order'
      }))
    }
  })
}

export async function updateOrderDates(
  id: number,
  attributes: Record<string, string>
): Promise<ReadResult<Order>> {
  await checkRoles([Role.ROOT, Role.MANAGER, Role.FINANCE])
  const { data } = await patch(`/orders/${id}/dates`, { data: { attributes } })
  return data
}

export async function updateOrderOwner(
  id: number,
  staffId: string
): Promise<ReadResult<Order>> {
  await checkRoles([Role.ROOT, Role.MANAGER])
  const { data } = await patch(`/orders/${id}/owner`, {
    data: { attributes: { staff_id: staffId } }
  })
  return data
}

export async function updateInstallmentDates(
  updates: { id: string; due_at: string }[]
): Promise<void> {
  await checkRoles([Role.ROOT, Role.MANAGER, Role.FINANCE])
  await patch('/order-installments/dates', { data: { updates } })
}

export async function updateItemWriteOff(
  orderId: number,
  attributes: { write_off_date: Date; write_off_invoice: string }
): Promise<void> {
  await checkRoles([Role.ROOT, Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  await patch(`/orders/${orderId}/items/write-off`, { data: { attributes } })
}

export async function assignEnterprise(
  orderId: number,
  itemIds: number[],
  enterpriseId: number
): Promise<void> {
  await checkRoles([Role.ROOT, Role.MANAGER, Role.FINANCE, Role.LOGISTICS])
  await patch(`/orders/${orderId}/items/assign-enterprise`, {
    data: { item_ids: itemIds, enterprise_id: enterpriseId }
  })
}
